import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getStyles } from './styles';
import SubSidebar from './Sidebar';

import { InvalidAccess } from '../../../utils/sessionDataHandle';
import ConnectorList from './Integrations/ConnectorList';
import ExtractionIntegrations from './Integrations';
import ExtractionTriggers from './Triggers';
import AddIntegration from './Integrations/AddIntegration';
import AddTrigger from './Triggers/AddTrigger';
import { useAppContext } from '../../../context/AppContext';
import { useDispatch, useSelector } from 'react-redux';
import getExtractionIntegrationData from '../../../utils/ApiFunctions/ExtractionIntegrationData';
import { setExtractionIntegrationsState, setExtractionTriggersState } from '../../../Redux/slice/dataSlice';
import { setExtractionIntegrationAdded, setExtractionTriggerAdded } from '../../../Redux/slice/newDataSlice';
import getExtractionTriggerData from '../../../utils/ApiFunctions/ExtractionTriggerData';
import MonitoringServiceApi from '../../../Api/Services/AmeyaBackend/MonitoringServiceApi';

function ExtractionConnectors() {

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const styles = getStyles(theme);
    const dispatch = useDispatch();

    const { selectedWorkspace, selectedProject } = useAppContext();
    const [selectedMenuItem, setSelectedMenuItem] = useState(0);

    const extraction_integrations = useSelector(state => state.all_data.extraction_integrations);
    const extraction_triggers = useSelector(state => state.all_data.extraction_triggers);
    const extraction_integration_added = useSelector(state => state.data_added.extraction_integration_added);
    const extraction_trigger_added = useSelector(state => state.data_added.extraction_trigger_added);

    const [dataLoading, setDataLoading] = useState(false);
    const [connectors, setConnectors] = useState([])
    const [triggers, setTriggers] = useState([])


    useEffect(() => {

        if (!selectedWorkspace || !selectedProject) {
            InvalidAccess(navigate);
            return
        }

        const selected_engine_name = selectedProject?.payload?.configuration?.engine_name

        if (location.pathname === '/connectors') {
            if (selected_engine_name === "extraction_agent") {
                navigate('/connectors/extraction-agent/connector-lists');
            }
        }

        if (location.pathname.startsWith('/connectors/extraction-agent/connector-lists')) {
            setSelectedMenuItem(0);
        }

    }, [location.pathname, navigate]);

    const handleMenuChange = (engine, path) => {
        if (engine && path) {
            navigate(`/connectors/${engine?.toLowerCase()?.replace(/_/g, '-')}/${path?.toLowerCase()?.replace(/_/g, '-')}`);
        }
    };

    useEffect(() => {
        fetchData()
    }, [extraction_integration_added, extraction_trigger_added])

    const fetchData = async () => {
        setDataLoading(true)
        try {
            await Promise.all([fetchIntegrationData(), fetchTriggerData()])
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)
        }
    }

    const fetchIntegrationData = async () => {
        try {
            if (extraction_integrations?.length > 0 && extraction_integration_added === false) {
                setConnectors(extraction_integrations)
            } else {
                const response = await getExtractionIntegrationData(selectedProject?.payload?.__auto_id__)
                setConnectors(response)
                dispatch(setExtractionIntegrationsState(response))
                dispatch(setExtractionIntegrationAdded(false))
            }
        } catch (error) {
            console.log(error)
        }
    }

    const checkJobStatus = async (triggerData = []) => {
        try {
            let updatedData = [...triggerData];

            for (const item of triggerData || []) {

                const job_id = item?.payload?.job_id ?? null;

                if (job_id) {
                    try {
                        const monitor_res = await MonitoringServiceApi.getJobStatus(job_id);

                        if (monitor_res.status === 200) {
                            const status = monitor_res?.data?.status;

                            if (status === "Failed") {
                                updatedData = updatedData.map(d => d === item
                                    ?
                                    {
                                        ...d,
                                        payload: {
                                            ...d.payload,
                                            monitoring_status: true,
                                            error_message: "Job Failed",
                                        },
                                    }
                                    : d
                                );
                            }
                        }
                    } catch (error) {
                        console.error("Error fetching monitoring status:", error);
                    }

                }
            }
            return updatedData;
        } catch (error) {
            console.error(error);
            return triggerData;
        }
    };

    const fetchTriggerData = async () => {
        try {
            let all_triggers = []
            if (extraction_triggers?.length > 0 && extraction_trigger_added === false) {
                all_triggers = extraction_triggers
            } else {
                const response = await getExtractionTriggerData(selectedProject?.payload?.__auto_id__)
                all_triggers = response
                dispatch(setExtractionTriggersState(response))
                dispatch(setExtractionTriggerAdded(false))
            }
            const filtered_triggers = (all_triggers || []).map(item => ({ ...item, payload: { ...item.payload, monitoring_status: false } })) ?? [];
            const updated_trigger = await checkJobStatus(filtered_triggers)
            setTriggers(updated_trigger)
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.sidebar}>
                <SubSidebar handleMenuChange={handleMenuChange} setSelectedMenuItem={setSelectedMenuItem} selectedMenuItem={selectedMenuItem} />
            </Box>
            <main style={styles.componentContainer}>
                <Box sx={styles.cardContainer}>
                    <Routes>
                        <Route path="extraction-agent/connector-lists" element={<ConnectorList setSelectedMenuItem={setSelectedMenuItem} />} />
                        <Route
                            path="extraction-agent/integrations"
                            element={
                                <ExtractionIntegrations
                                    dataLoading={dataLoading}
                                    connectors={connectors}
                                />
                            }
                        />
                        <Route
                            path="extraction-agent/connector-lists/add"
                            element={<AddIntegration setSelectedMenuItem={setSelectedMenuItem} />}
                        />
                        <Route
                            path="extraction-agent/triggers"
                            element={
                                <ExtractionTriggers
                                    triggers={triggers}
                                    dataLoading={dataLoading}
                                />
                            }
                        />
                        <Route
                            path="extraction-agent/triggers/add"
                            element={
                                <AddTrigger
                                    setSelectedMenuItem={setSelectedMenuItem}
                                    connector_loading={dataLoading}
                                    connectors={connectors}
                                />
                            }
                        />
                    </Routes>
                </Box>
            </main>
        </Box>
    )
}

export default ExtractionConnectors;